import { ClusterPointComponent } from '../cluster-point/cluster-point.component';

import { MemoizePipe } from '../../memoize.pipe';
import { IsNilPipe } from 'src/app/pipes/is-nil.pipe';

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Injector,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  ClusterPointDirective,
  ControlComponent,
  FullscreenControlDirective,
  GeoJSONSourceComponent,
  MapComponent as NgxMapboxMapComponent,
  MarkerComponent,
  MarkersForClustersComponent,
  NavigationControlDirective,
  PointDirective,
  PopupComponent,
} from 'ngx-mapbox-gl';

import {
  FaIconComponent,
  FaLayersComponent,
  FaLayersTextComponent,
} from '@fortawesome/angular-fontawesome';

import { isNil } from '../../../../utils/utils';
import { LngLatBoundsLike, LngLatLike, Map } from 'mapbox-gl';
import { Subscription } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatButtonModule,
  MatIconModule,
  MatCardModule,
  MatListModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  ClusterPointComponent,
  FaLayersComponent,
  FaLayersTextComponent,
  NgxMapboxMapComponent,
  ControlComponent,
  GeoJSONSourceComponent,
  MarkersForClustersComponent,
  MarkerComponent,
  PopupComponent,
];
export const PIPES = [IsNilPipe, MemoizePipe];
export const DIRECTIVES = [
  NavigationControlDirective,
  FullscreenControlDirective,
  ClusterPointDirective,
  PointDirective,
];

/**
 * Component that allows clustering markers in a map. It receives popup element from parent component using the `popup` attribute.
 * @typedef MapLayer
 * @prop {string} name The street
 * @prop {string} style The City
 *
 * @param {string} style  The style property `mgl-map` the map will use.
 * @param {LngLatBoundsLike} bounds  The fitBounds property `mgl-map` the map will use.
 * @param {MapLayer[]} mapLayers  The map layers that will be displayed to be selected.
 * @param {string} activeMapLayerName  The current active map layer.
 * @param {any} webcamImages  Latest webcam images.
 * @param {Function} getMarkerColorClassFn  Function that decides the color class of the markers.
 * @param {Function} getGmaAlarmMarkerColorFn  Function that decides the color of GmaAlarm markers.
 * @param {GeoJSON.FeatureCollection<GeoJSON.Point>} data  Complete data that once filtered by selected parameter, will be passed to `mgl-geojson-source` component.
 * @param {string[]} tabParameters  The parameters that can be selected into the tabs
 * @param {EventEmitter<string>} setActiveMapLayerByName  When a map layer is selected communicates the parent about the change
 */
@Component({
  selector: 'app-cluster-html',
  templateUrl: './cluster-html.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class ClusterHtmlComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private injector = inject(Injector);
  private ref = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  style = input<string>();
  bounds = input<LngLatBoundsLike>();
  mapLayers = input<
    {
      name: string;
      style: string;
    }[]
  >([]);
  activeMapLayerName = input<string>();
  webcamImages = input<any>();
  getMarkerColorClassFn = input<Function>();
  getGmaAlarmMarkerColorFn = input<Function>();
  data = input<GeoJSON.FeatureCollection<GeoJSON.Point>>();
  tabParameters = input<any[]>([]);

  setActiveMapLayerByName = output<string>();
  mapLoad = output<{
    type: any;
    target: Map;
  }>();

  showLayerSelect = signal(false);
  map: Map;
  mapBoxConfiguration = signal({
    zoom: [8] as [number],
    maxZoom: 13,
    center: [0, 20] as LngLatLike,
  });
  mapParamatersConfiguration = signal<any>({});
  filteredData = signal<GeoJSON.FeatureCollection<GeoJSON.Point>>(undefined);

  selectedStation = signal<GeoJSON.Feature<GeoJSON.Point>>(undefined);
  selectedParameter = signal<string>(undefined);

  subscriptions: Subscription[] = [];

  constructor() {
    this.listenToDataChanges();
  }

  ngOnInit(): void {
    this.listenToLanguageChanges();
    this.selectedParameter.set(this.tabParameters()[0]);
    this.updateFilteredData(this.selectedParameter());
    this.initMapParametersConfiguration();
    this.ref.detectChanges();
  }

  ngAfterViewInit(): void {
    if (!isNil(this.map)) this.map.resize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].isFirstChange()) {
      this.updateFilteredData(this.selectedParameter());
    }
  }

  onChangeParameter(type: string) {
    this.selectedParameter.set(type);
    this.updateFilteredData(type);
    this.ref.detectChanges();
  }

  onClickStation(feature: GeoJSON.Feature<GeoJSON.Point>, e?: MouseEvent) {
    if (!isNil(e)) e.preventDefault();
    setTimeout(() => {
      if (feature?.geometry?.coordinates) {
        this.selectedStation.set(feature);
        this.ref.detectChanges();
      }
    });
  }

  flyTo(coordinates: GeoJSON.Position) {
    this.map.flyTo({ center: coordinates as LngLatLike });
  }

  onMapLoad(ev: { type: any; target: Map }) {
    const map = ev.target;
    this.map = map;

    setTimeout(() => {
      map.resize();
      map.fitBounds(this.bounds(), {
        padding: 100,
        duration: 0,
        maxZoom: this.mapBoxConfiguration().maxZoom,
      });
      this.changeMapboxLangTo(this.translateService.getCurrentLang());
    });

    this.mapLoad.emit(ev);
  }

  onMapCreated(map: Map) {
    this.map = map;
  }

  onSetActiveMapLayer(mapLayerName: string) {
    this.setActiveMapLayerByName.emit(mapLayerName);
    this.ref.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => {
      s.unsubscribe();
    });
  }

  private initMapParametersConfiguration() {
    this.tabParameters().forEach(parameter => {
      this.mapParamatersConfiguration.update(mapParamatersConfiguration => {
        return {
          ...mapParamatersConfiguration,
          [parameter]: {
            clusterSourceId: 'cluster_source_id_' + parameter,
          },
        };
      });
    });
  }

  private updateFilteredData(parameter: string) {
    const data = this.data();
    if (!data?.features) return;

    this.filteredData.set({
      ...data,
      features: data.features.filter(
        feature => !isNil(feature.properties[parameter])
      ),
    });
  }

  private listenToLanguageChanges() {
    const subscription = this.translateService.onLangChange.subscribe(ev =>
      this.changeMapboxLangTo(ev.lang)
    );
    this.subscriptions.push(subscription);
  }

  private changeMapboxLangTo(language: string) {
    if (!this.map) return;

    if (language === 'nl') language = 'de';

    const layerId = 'country-label';
    if (!this.map.getLayer(layerId)) {
      return;
    }
    this.map.setLayoutProperty(
      layerId,
      'text-field',
      '{name_' + language + '}'
    );
  }

  private listenToDataChanges() {
    effect(
      () => {
        this.data();
        this.selectedParameter();

        untracked(() => {
          this.updateFilteredData(this.selectedParameter());
        });
      },
      { injector: this.injector }
    );
  }
}
